import { Expense } from "@finway-group/shared/lib/models"
import type { ExpenseOposTotalsByCurrency } from "@finway-group/shared/lib/models/expense/expense.api"
import { type InvoiceLinkRequest } from "@finway-group/shared/lib/models/expense/linkRequest.interface"
import { useMutation, useQuery, type UseQueryOptions } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { invalidatePurchaseOrderDetail, invalidatePurchaseOrderLists } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { invalidateCardByExpenseQuery, invalidateCardQuery } from "~Shared/queries/card.queries"
import { convertSortingToString, getSortQueryObject } from "~Shared/queries/query.utils"
import { ExpenseHttpService } from "~Shared/services/expense.http.service"
import { TableService } from "~Shared/services/table.service"
import { onExpenseUpdate } from "~Shared/store/actions/expense/expenseActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { queryGet, queryPost } from "~Shared/utils/reactQuery.utils"
import { getTableDefaultSort } from "~Shared/utils/table.utils"
import { CacheEnum, type QueryParams, type ReactQueryTableData } from "../consts"

const MODEL = "expenses"
const staleTime = 60 * 1000
type Options = Omit<UseQueryOptions<Expense>, "queryKey" | "queryFn" | "select" | "staleTime">

/** Build query params for any table list (pagination, sort, search, filter). */
export const buildTableListQueryParams = (table: TablesEnum, params: QueryParams) => {
    const { page = 1, limit = 20, search = "", filter = {}, sortParams } = params
    const sorting = getSortQueryObject(sortParams)
    const sortQueryString = sorting.sortBy ? `&sortBy=${sorting.sortBy}` : convertSortingToString(getTableDefaultSort(table))
    const baseFilter = TableService.getTableBaseFilterQueryString(table)
    const filterQueryString = baseFilter + TableService.getTableFilterQueryString(table, filter)

    const searchQueryString = TableService.getTableSearchQueryString(table, search)
    return { page, limit, filterQueryString, sortQueryString, searchQueryString }
}

const fetchOposList = async (params: QueryParams, signal?: AbortSignal): Promise<ReactQueryTableData<Expense>> => {
    const queryParams = buildTableListQueryParams(TablesEnum.OPOS_LIST, params)
    const data = await ExpenseHttpService.fetchAllExpenses({ queryParams, signal })
    const docs = (data.docs || []).map((doc: any) => new Expense(doc))
    return { ...data, docs }
}

const fetchOposExcludedList = async (params: QueryParams, signal?: AbortSignal): Promise<ReactQueryTableData<Expense>> => {
    const queryParams = buildTableListQueryParams(TablesEnum.OPOS_EXCLUDED_LIST, params)
    const data = await ExpenseHttpService.fetchAllExpenses({ queryParams, signal })
    const docs = (data.docs || []).map((doc: any) => new Expense(doc))
    return { ...data, docs }
}

export const useExpense = (expenseId: string, opts?: Options) =>
    useQuery({
        ...opts,
        queryKey: [CacheEnum.EXPENSES, expenseId],
        queryFn: () => queryGet(`${MODEL}/${expenseId}`),
        select: (expense: Expense) => new Expense(expense),
        staleTime,
    })

export const useExpensesByIds = ({ ids }: { ids: Array<string> }) =>
    useQuery({
        queryKey: [CacheEnum.EXPENSES_MANY, ids],
        queryFn: ({ signal }) => ExpenseHttpService.fetchExpenses(`&_id[in]=${ids}`, undefined, undefined, undefined, { signal }),
        select: (result: { expenses: Array<Expense> }) => result.expenses,
        staleTime,
        enabled: ids.length > 0,
    })

export const useOposList = (params: QueryParams, opts?: Pick<UseQueryOptions<ReactQueryTableData<Expense>>, "enabled">) => {
    return useQuery({
        queryKey: [CacheEnum.EXPENSES_OPOS_LIST, params],
        queryFn: ({ signal }) => fetchOposList(params, signal),
        staleTime,
        enabled: opts?.enabled ?? true,
    })
}

export const useOposExcludedList = (params: QueryParams, opts?: Pick<UseQueryOptions<ReactQueryTableData<Expense>>, "enabled">) => {
    return useQuery({
        queryKey: [CacheEnum.EXPENSES_OPOS_EXCLUDED_LIST, params],
        queryFn: ({ signal }) => fetchOposExcludedList(params, signal),
        staleTime,
        enabled: opts?.enabled ?? true,
    })
}

export const useOposTotals = (params: QueryParams, opts?: Pick<UseQueryOptions<Array<ExpenseOposTotalsByCurrency>>, "enabled">) => {
    return useQuery({
        queryKey: [CacheEnum.EXPENSES_OPOS_TOTALS, params],
        queryFn: () => {
            const queryParams = buildTableListQueryParams(TablesEnum.OPOS_LIST, params)
            return ExpenseHttpService.fetchOposTotals(`${queryParams.filterQueryString}${queryParams.searchQueryString}`)
        },
        select: (data): Array<ExpenseOposTotalsByCurrency> => data.totalsByCurrency,
        staleTime,
        enabled: opts?.enabled ?? true,
    })
}

export { invalidateAllOposLists, invalidateOposExcludedList, invalidateOposList, invalidateOposTotals } from "./oposList.invalidation"

export const invalidateExpense = async (expenseId: string) => {
    const cachedExpense = queryClient.getQueryData<Expense>([CacheEnum.EXPENSES, expenseId])
    const cardId = cachedExpense?.card

    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [CacheEnum.EXPENSES, expenseId],
        }),
        queryClient.invalidateQueries({
            queryKey: [CacheEnum.EXPENSES_MANY],
        }),
        invalidateCardByExpenseQuery(expenseId),
        ...(cardId ? [invalidateCardQuery(cardId)] : []),
    ])
}

export const useLinkPurchaseOrderMutation = () => {
    const dispatch = useDispatch()
    return useMutation<Expense, Error, { expenseId: string; linkRequest: InvoiceLinkRequest }>({
        mutationFn: async ({ expenseId, linkRequest }: { expenseId: string; linkRequest: InvoiceLinkRequest }) => {
            const response = await queryPost<InvoiceLinkRequest, Expense>(`/${MODEL}/${expenseId}/link-purchase-orders`, linkRequest)
            return new Expense(response)
        },
        onSuccess: async (data, variables) => {
            const { expenseId, linkRequest } = variables
            const promises = [
                invalidatePurchaseOrderLists(),
                invalidateExpense(expenseId),
                ...linkRequest.links.map((link) => invalidatePurchaseOrderDetail(link.poId)),
                ...linkRequest.unlinks.map((unlink) => invalidatePurchaseOrderDetail(unlink.poId)),
                dispatch(onExpenseUpdate(data)),
            ]

            await Promise.all(promises)
        },
    })
}
